import { get } from "svelte/store"
import { _ } from "svelte-i18n"
import { normalizeVat, setEndCustomerPriceByPCalc } from "../../../../api/hooks/config-client"
import { getDealer } from "../../api"
import { newNotification } from "../../store"

export function calculateDiscountPercent(
    price: number,
    surchargePercent: number,
    aPS: number,
    newTotal: number,
    // nur `quantity` wird benötigt; der Pricewizard übergibt eine
    // Partial<OperationPositionOut>-Sicht
    position: Partial<OperationPositionOut>
) {
    let discountPercent = 0
    const accuracy = 0.000001
    let difference
    let maxIts = 10000000
    do {
        let currentTotal = price * (1 + surchargePercent / 100) * (1 - discountPercent / 100) * position?.quantity + aPS
        difference = newTotal - currentTotal

        discountPercent -= difference / 10000
        maxIts -= 1
        if (maxIts < 0) break
    } while (Math.abs(difference) > accuracy)

    return discountPercent
}

// Wird ausschließlich im operation-out-Zweig aufgerufen (Preisfelder inkl.
// priceWizard existieren nur dort).
export function updatePriceWizard(position: OperationPositionOut) {
    if (!position.priceWizard)
        position.priceWizard = {
            discount: 0,
            surcharge: 0,
        }
    position.priceWizard.discount = calculateDiscountPercent(
        position.netPrice,
        position.priceWizard.surcharge,
        position?.additionalItems?.reduce(
            (acc: number, cur: { netPrice: number; amount: number }) => acc + cur.netPrice * cur.amount,
            0
        ) || 0,
        position.endCustomerPrice * position.quantity +
            (position?.additionalItems?.map((p) => p?.netPrice * p?.amount).reduce((a, b) => a + b, 0) || 0),
        position
    )
    return position
}

// Pure function
function calculateValidUntil(daysValid: number, expiryDates: Array<any>): Date {
    const today = new Date()
    let validUntil = new Date()
    validUntil.setDate(validUntil.getDate() + daysValid)
    expiryDates?.forEach((e) => {
        const expDate = new Date(e.date)
        if (expDate > today && expDate < validUntil) {
            validUntil = expDate
        }
    })
    return validUntil
}
export const applyPCalc = async (
    pCalc: PriceCalculation,
    operation: OperationOut,
    updateOperation: any = () => {},
    formData: Address = null
): Promise<OperationOut | [OperationOut, Address]> => {
    let dealer = (await getDealer({ dealerNumber: operation.dealerNumber })).data[0]
    let vat = normalizeVat(dealer?.vat)
    let daysValid = pCalc.daysValid
    let validUntil = calculateValidUntil(daysValid, pCalc?.expiryDates)

    let sumEndCustomerPrice = 0
    let sumEndCustomerPriceGross = 0

    // Dieselbe Regel wie im Hook (api/hooks/lib/helper.js, getSumEndCustomerPrice):
    // eine Position ohne Preis bleibt **unberchnet** und macht die Summe des
    // Dokuments unvollstaendig, statt `NaN` in die Angebotsliste zu schreiben
    // (B16). Ohne UVP gibt es keinen kalkulierbaren Endkundenpreis - und
    // "unberechnet" ist ehrlicher als ein erfundener Wert.
    let unvollstaendig = false
    operation.positions.forEach((p: OperationPositionOut) => {
        setEndCustomerPriceByPCalc(p, pCalc, operation)
        if (!Number.isFinite(p.endCustomerPrice)) {
            delete p.endCustomerPrice
            delete p.endCustomerPriceGross
            unvollstaendig = true
            return
        }
        if (!p.priceWizard) p.priceWizard = { surcharge: 0, discount: 0 }
        p.priceWizard.surcharge = (p.endCustomerPrice / p.netPrice - 1) * 100
        p.priceWizard.discount = 0
        p.endCustomerPriceGross = calculatePriceWithVAT(p.endCustomerPrice, vat)

        sumEndCustomerPrice += p.endCustomerPrice
        sumEndCustomerPriceGross += p.endCustomerPriceGross
    })

    operation.sumEndCustomerPrice = unvollstaendig ? undefined : sumEndCustomerPrice
    operation.sumEndCustomerPriceGross = unvollstaendig ? undefined : sumEndCustomerPriceGross
    operation.endCustomerVAT = vat
    operation.validUntil = validUntil
    operation.appliedPCalc = pCalc.id
    newNotification({
        class: "success",
        html: get(_)("components.pcalcGroup.gotApplied"),
        timeout: 3000,
    })
    if (formData) {
        operation = updateOperation(operation, formData)

        return [operation, formData]
    }
    return operation
}

export const calculatePriceWithVAT = (netPrice: number, VAT: number) => {
    return Math.round(netPrice * (1 + VAT / 100) * 100) / 100
}

export const applyPCalcOnSinglePosition = async (pCalc: PriceCalculation, operation: any, positionIndex: number) => {
    let dealer = (await getDealer({ dealerNumber: operation.dealerNumber })).data[0]
    let vat = normalizeVat(dealer?.vat)
    let daysValid = pCalc.daysValid
    let validUntil = calculateValidUntil(daysValid, pCalc?.expiryDates)

    let sumEndCustomerPrice = 0
    let sumEndCustomerPriceGross = 0
    operation.positions.forEach((p: OperationPositionOut, i: number) => {
        if (i == positionIndex) {
            setEndCustomerPriceByPCalc(p, pCalc, operation)
            if (!p.priceWizard) p.priceWizard = { surcharge: 0, discount: 0 }
            p.priceWizard.surcharge = (p.endCustomerPrice / p.netPrice - 1) * 100
            p.endCustomerPriceGross = calculatePriceWithVAT(p.endCustomerPrice, vat)
        }
        sumEndCustomerPrice += p.endCustomerPrice
        sumEndCustomerPriceGross += p.endCustomerPriceGross
    })

    operation.sumEndCustomerPrice = sumEndCustomerPrice
    operation.sumEndCustomerPriceGross = sumEndCustomerPriceGross
    operation.endCustomerVAT = vat
    operation.validUntil = validUntil

    newNotification({
        class: "success",
        html: get(_)("components.pcalcGroup.gotApplied"),
        timeout: 3000,
    })

    return operation
}
